// src/pages/client/MyProfile.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Paper,
  Avatar,
  Button,
  Divider,
  TextField,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Grid,
  Badge,
  useTheme,
  alpha,
  MenuItem,
} from "@mui/material";

// Ícones
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";

import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

import {
  FaMusic,
  FaTheaterMasks,
  FaLaughBeam,
  FaChalkboardTeacher,
  FaUsers,
  FaTools,
  FaBriefcase,
  FaGlassCheers,
  FaGhost,
  FaHeadphonesAlt,
  FaFutbol,
  FaRunning,
  FaPalette,
  FaUtensils,
  FaChild,
  FaVrCardboard,
  FaPrayingHands,
  FaGamepad,
  FaMapMarkedAlt,
} from "react-icons/fa";

import CelebrationIcon from "@mui/icons-material/Celebration";
import EventIcon from "@mui/icons-material/Event";

import { rtdb } from "../../services/firebase/config";

// RTDB
import { ref as rRef, update as rUpdate, get as rGet } from "firebase/database";

export default function MyProfile() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout, updateProfileData } = useAuth();

  // ---------------------------------------------------------------------------
  // ESTADOS
  // ---------------------------------------------------------------------------
  const [tab, setTab] = useState(0);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingBasic, setSavingBasic] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);

  const [dirtyBasic, setDirtyBasic] = useState(false);
  const [dirtyAddress, setDirtyAddress] = useState(false);

  const [openPass, setOpenPass] = useState(false);
  const [pass, setPass] = useState({ current: "", next: "", confirm: "" });

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const AVATAR_ICONS = {
    music: FaMusic,
    theater: FaTheaterMasks,
    laugh: FaLaughBeam,
    teacher: FaChalkboardTeacher,
    users: FaUsers,
    tools: FaTools,
    briefcase: FaBriefcase,
    party: FaGlassCheers,
    ghost: FaGhost,
    headphones: FaHeadphonesAlt,
    soccer: FaFutbol,
    running: FaRunning,
    palette: FaPalette,
    food: FaUtensils,
    child: FaChild,
    vr: FaVrCardboard,
    pray: FaPrayingHands,
    game: FaGamepad,
    map: FaMapMarkedAlt,
    celebration: CelebrationIcon,
    event: EventIcon,
  };

  const DEFAULT_AVATAR = "users";

  const [avatarIcon, setAvatarIcon] = useState(DEFAULT_AVATAR);
  const [openAvatarModal, setOpenAvatarModal] = useState(false);

  function onlyDigits(v) {
    return (v || "").replace(/\D/g, "");
  }

  // Nome: Title Case por palavra, mantendo espaço no final quando o user aperta espaço
  function formatFullName(v) {
    const raw = (v || "").replace(/\s+/g, " ");
    const endsWithSpace = /\s$/.test(raw);
    const cleaned = raw.replace(/[^A-Za-zÀ-ÿ\s'-]/g, "");

    const formatted = cleaned
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

    return endsWithSpace ? `${formatted} ` : formatted;
  }

  // Telefone: só números, 11 dígitos, formato (32)998115007
  function formatPhoneBR11(v) {
    const d = onlyDigits(v).slice(0, 11);
    if (d.length === 0) return "";
    if (d.length <= 2) return `(${d}`;
    return `(${d.slice(0, 2)})${d.slice(2)}`;
  }

  // CPF: 000.000.000-00 (11 dígitos)
  function formatCPF(v) {
    const d = onlyDigits(v).slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }

  // Nascimento: dd/mm/aaaa (8 dígitos)
  function formatBirthBR(v) {
    const d = onlyDigits(v).slice(0, 8);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  }

  // CEP: só números, 8 dígitos (sem máscara)
  function formatCEP(v) {
    return onlyDigits(v).slice(0, 8);
  }

  const BR_UFS = [
    { uf: "AC", name: "Acre" },
    { uf: "AL", name: "Alagoas" },
    { uf: "AP", name: "Amapá" },
    { uf: "AM", name: "Amazonas" },
    { uf: "BA", name: "Bahia" },
    { uf: "CE", name: "Ceará" },
    { uf: "DF", name: "Distrito Federal" },
    { uf: "ES", name: "Espírito Santo" },
    { uf: "GO", name: "Goiás" },
    { uf: "MA", name: "Maranhão" },
    { uf: "MT", name: "Mato Grosso" },
    { uf: "MS", name: "Mato Grosso do Sul" },
    { uf: "MG", name: "Minas Gerais" },
    { uf: "PA", name: "Pará" },
    { uf: "PB", name: "Paraíba" },
    { uf: "PR", name: "Paraná" },
    { uf: "PE", name: "Pernambuco" },
    { uf: "PI", name: "Piauí" },
    { uf: "RJ", name: "Rio de Janeiro" },
    { uf: "RN", name: "Rio Grande do Norte" },
    { uf: "RS", name: "Rio Grande do Sul" },
    { uf: "RO", name: "Rondônia" },
    { uf: "RR", name: "Roraima" },
    { uf: "SC", name: "Santa Catarina" },
    { uf: "SP", name: "São Paulo" },
    { uf: "SE", name: "Sergipe" },
    { uf: "TO", name: "Tocantins" },
  ];

  // ---------------------------------------------------------------------------
  // DADOS (userData) + FORM
  // ---------------------------------------------------------------------------
  const userData = useMemo(() => {
    const u = user || {};
    return {
      uid: u.uid || "—",
      // (pode vir do Auth, mas vamos sobrescrever com Firestore se existir)
      name: u.displayName || u.name || "Cliente",
      email: u.email || "",
      phone: u.phoneNumber || u.phone || "",
      photoURL: u.photoURL || u.photo || "",
      isVerified: Boolean(u.emailVerified || u.verified),
    };
  }, [user]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    cpf: "",
    birth: "",
  });

  const [address, setAddress] = useState({
    cep: "",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
  });

  const [prefs, setPrefs] = useState({
    whatsapp: true,
    email: true,
    sms: false,
    marketing: true,
  });

  // ---------------------------------------------------------------------------
  // ESTILOS (SX)
  // ---------------------------------------------------------------------------
  const pageBgSx = {
    minHeight: "100vh",
    pb: { xs: 5, md: 8 },
    pt: { xs: 5, md: 8 },
    background: `
      radial-gradient(circle at 15% 15%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 40%),
      radial-gradient(circle at 85% 30%, ${alpha("#08162b", 0.8)} 0%, transparent 60%)
    `,
    backgroundColor: theme.palette.background.default,
  };

  const glassCardSx = {
    borderRadius: 1,
    border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
    background: alpha(theme.palette.background.paper, 0.6),
    backdropFilter: "blur(20px)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    overflow: "hidden",
  };

  // ---------------------------------------------------------------------------
  // HELPERS DE SET + FLAGS
  // ---------------------------------------------------------------------------
  function setField(key, value) {
    setForm((p) => ({ ...p, [key]: value }));
    setDirtyBasic(true);
  }

  function setAddr(key, value) {
    setAddress((p) => ({ ...p, [key]: value }));
    setDirtyAddress(true);
  }

  function setPref(key, value) {
    setPrefs((p) => ({ ...p, [key]: value }));
    // se quiser salvar prefs depois, dá pra criar dirtyPrefs
  }

  // ---------------------------------------------------------------------------
  // RTDB: carregar dados base do usuário (role, nome, email)
  // ---------------------------------------------------------------------------
  const loadFromRTDB = useCallback(async () => {
    if (!user?.uid) return;

    setLoadingProfile(true);

    try {
      const snap = await rGet(rRef(rtdb, `users/${user.uid}`));

      const base = {
        name: userData.name || "",
        email: userData.email || "",
        phone: userData.phone || "",
        cpf: "",
        birth: "",
      };

      if (!snap.exists()) {
        setForm(base);
        setAddress({
          cep: "",
          street: "",
          number: "",
          complement: "",
          district: "",
          city: "",
          state: "",
        });
        setPrefs({ whatsapp: true, email: true, sms: false, marketing: true });
      } else {
        const d = snap.val() || {};

        if (d.avatarIcon) setAvatarIcon(d.avatarIcon);

        setForm({
          name: d.name || d.displayName || base.name,
          email: d.email || base.email,
          phone: d.phone || d.phoneNumber || base.phone,
          cpf: d.cpf || "",
          birth: d.birth || "",
        });

        const pr = d.preferences || null;
        setPrefs(
          pr
            ? {
                whatsapp: pr.whatsapp ?? true,
                email: pr.email ?? true,
                sms: pr.sms ?? false,
                marketing: pr.marketing ?? true,
              }
            : { whatsapp: true, email: true, sms: false, marketing: true },
        );
      }

      setDirtyBasic(false);
      setDirtyAddress(false);
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        msg: "Não foi possível carregar seu perfil.",
      });
    } finally {
      setLoadingProfile(false);
    }
  }, [user?.uid, userData.name, userData.email, userData.phone, userData, user]);

  const loadProfileFromRTDB = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const profileRef = rRef(rtdb, `users/${user.uid}`);
      const snap = await rGet(profileRef);

      if (!snap.exists()) return;

      const p = snap.val();

      if (p.avatarIcon) {
        setAvatarIcon(p.avatarIcon);
      }

      // 🔹 dados básicos editáveis
      setForm((prev) => ({
        ...prev,
        name: p.name ?? prev.name,
        email: p.email ?? prev.email,
        phone: p.phone ?? prev.phone,
        cpf: p.cpf ?? "",
        birth: p.birth ?? "",
      }));

      // 🔹 endereço (RTDB é soberano aqui)
      if (p.address) {
        setAddress({
          cep: p.address.cep || "",
          street: p.address.street || "",
          number: p.address.number || "",
          complement: p.address.complement || "",
          district: p.address.district || "",
          city: p.address.city || "",
          state: p.address.state || "",
        });
      }
    } catch (err) {
      console.error("Erro ao carregar profile RTDB", err);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    (async () => {
      setLoadingProfile(true);
      await loadFromRTDB();
      await loadProfileFromRTDB();
      setLoadingProfile(false);
    })();
  }, [user?.uid, loadFromRTDB, loadProfileFromRTDB]);

  // salvar avatar
  async function saveAvatarIcon() {
    if (!user?.uid) return;

    try {
      const profileRef = rRef(rtdb, `users/${user.uid}`);
      await rUpdate(profileRef, {
        avatarIcon,
        updatedAt: Date.now(),
      });

      setToast({
        open: true,
        type: "success",
        msg: "Ícone de perfil atualizado!",
      });
    } catch (err) {
      setToast({
        open: true,
        type: "error",
        msg: "Erro ao salvar ícone.",
      });
    }
  }

  // ---------------------------------------------------------------------------
  // RTDB: salvar dados do perfil (campos extras preenchidos pelo usuário)
  // ---------------------------------------------------------------------------
  async function saveBasicProfile() {
    if (!user?.uid) return;

    try {
      setSavingBasic(true);

      const payload = {
        name: (form.name || "").trim(),
        email: (form.email || "").trim(),
        phone: onlyDigits(form.phone || ""),
        cpf: onlyDigits(form.cpf || ""),
        birth: (form.birth || "").trim(),
      };

      const profileRef = rRef(rtdb, `users/${user.uid}`);
      await rUpdate(profileRef, {
        ...payload,
        updatedAt: Date.now(),
      });

      setToast({ open: true, type: "success", msg: "Informações salvas." });
      setDirtyBasic(false);
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        msg: "Erro ao salvar informações.",
      });
    } finally {
      setSavingBasic(false);
    }
  }

  async function saveAddressToRTDB() {
    if (!user?.uid) return;

    try {
      setSavingAddress(true);

      //  define o ref AQUI
      const profileRef = rRef(rtdb, `users/${user.uid}`);

      await rUpdate(profileRef, {
        address: {
          cep: onlyDigits(address.cep || "").slice(0, 8),
          street: address.street || "",
          number: address.number || "",
          complement: address.complement || "",
          district: address.district || "",
          city: address.city || "",
          state: address.state || "",
        },
        updatedAt: Date.now(),
      });
      setToast({ open: true, type: "success", msg: "Endereço salvo." });
      setDirtyAddress(false);
    } catch (e) {
      setToast({ open: true, type: "error", msg: "Erro ao salvar endereço." });
    } finally {
      setSavingAddress(false);
    }
  }

  // ---------------------------------------------------------------------------
  // OUTRAS FUNÇÕES
  // ---------------------------------------------------------------------------
  async function handleChangePassword() {
    if (!pass.current || !pass.next || !pass.confirm) {
      setToast({
        open: true,
        type: "warning",
        msg: "Preencha todos os campos.",
      });
      return;
    }
    if (pass.next !== pass.confirm) {
      setToast({ open: true, type: "warning", msg: "Senhas não conferem." });
      return;
    }

    try {
      // ✅ plugar reauth + updatePassword quando quiser
      await new Promise((r) => setTimeout(r, 600));
      setToast({ open: true, type: "success", msg: "Senha alterada." });
      setOpenPass(false);
      setPass({ current: "", next: "", confirm: "" });
    } catch {
      setToast({ open: true, type: "error", msg: "Erro ao alterar senha." });
    }
  }

  function copyUid() {
    navigator.clipboard?.writeText(userData.uid);
    setToast({ open: true, type: "success", msg: "ID copiado." });
  }

  async function handleLogout() {
    try {
      await logout?.();
      navigate("/");
    } catch {
      setToast({ open: true, type: "error", msg: "Falha ao sair." });
    }
  }

  const AvatarIconComponent = AVATAR_ICONS[avatarIcon] || FaUsers;

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <Box sx={pageBgSx}>
      <Container maxWidth="lg">
        {/* === HEADER PRINCIPAL === */}
        <Box sx={{ mb: 4 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            spacing={3}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{
                  color: "white",
                  mb: 1,
                  fontSize: { xs: "h5.fontSize", md: "h3.fontSize" },
                }}
              >
                Meu Perfil
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  maxWidth: 520,
                  fontSize: { xs: "body2.fontSize", md: "body1.fontSize" },
                }}
              >
                Gerencie suas informações pessoais, endereços e configurações de
                segurança em um só lugar.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* === HERO (Perfil + Navegação) === */}
        <Paper sx={{ ...glassCardSx, mb: 4 }}>
          <Box sx={{ p: { xs: 2.25, sm: 3, md: 4 } }}>
            <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
              {/* LEFT: Avatar + Infos */}
              <Grid item xs={12} md={7}>
                <Stack
                  direction={{ xs: "row", sm: "row" }}
                  spacing={{ xs: 2, sm: 2.5 }}
                  alignItems={{ xs: "center", sm: "center" }}
                  sx={{
                    textAlign: { xs: "left", sm: "left" },
                  }}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                      <Tooltip
                        title={
                          userData.isVerified ? "Verificado" : "Não verificado"
                        }
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            bgcolor: userData.isVerified
                              ? theme.palette.primary.main
                              : theme.palette.warning.main,
                            color: theme.palette.background.paper,
                            display: "grid",
                            placeItems: "center",
                            border: `3px solid ${theme.palette.background.paper}`,
                          }}
                        >
                          <VerifiedUserRoundedIcon sx={{ fontSize: 18 }} />
                        </Box>
                      </Tooltip>
                    }
                  >
                    <Avatar
                      onClick={() => setOpenAvatarModal(true)}
                      alt={form.name || "Cliente"}
                      sx={{
                        width: { xs: 76, sm: 84, md: 100 },
                        height: { xs: 76, sm: 84, md: 100 },
                        border: `4px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                        boxShadow: "0 10px 26px rgba(0,0,0,0.55)",
                        bgcolor: alpha(theme.palette.common.white, 0.06),
                        color: theme.palette.primary.main,
                        cursor: "pointer",
                        transition: "all .2s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.35)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "grid",
                          placeItems: "center",
                          fontSize: { xs: 32, sm: 34, md: 42 },
                        }}
                      >
                        <AvatarIconComponent />
                      </Box>
                    </Avatar>
                  </Badge>

                  <Box
                    sx={{
                      minWidth: 0,
                      width: "100%",
                      display: "flex",
                      alignItems: "start",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "white",
                        fontWeight: 900,
                        lineHeight: 1.15,
                        typography: { xs: "h6", sm: "h5" },
                        mb: 0.75,
                        wordBreak: "break-word",
                      }}
                    >
                      {form.name || userData.name}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      justifyContent={{ xs: "left", sm: "flex-start" }}
                      sx={{ gap: 1 }}
                    >
                      <Chip
                        size="small"
                        label={form.email || userData.email || "Sem e-mail"}
                        icon={
                          <EmailRoundedIcon
                            sx={{ fontSize: "16px !important" }}
                          />
                        }
                        sx={{
                          maxWidth: "100%",
                          bgcolor: alpha(theme.palette.common.white, 0.05),
                          border: "1px solid rgba(255,255,255,0.1)",
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: { xs: 220, sm: 260, md: 320 },
                          },
                        }}
                      />

                      <Chip
                        size="small"
                        label={`UID: ${userData.uid?.slice?.(0, 8) || "—"}...`}
                        onClick={copyUid}
                        icon={
                          <ContentCopyRoundedIcon
                            sx={{ fontSize: "14px !important" }}
                          />
                        }
                        sx={{
                          bgcolor: "transparent",
                          border: "1px dashed rgba(255,255,255,0.2)",
                          cursor: "copy",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              {/* RIGHT: Buttons */}
              <Grid item xs={12} md={5}>
                <Stack
                  direction="row"
                  useFlexGap
                  flexWrap="wrap"
                  justifyContent={{ xs: "center", md: "flex-end" }}
                  spacing={1.25}
                  sx={{
                    gap: 1.25,
                    pt: { xs: 1, md: 0 },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<ConfirmationNumberRoundedIcon />}
                    onClick={() => navigate("/meus-ingressos")}
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      color: "text.secondary",
                      borderColor: "rgba(255,255,255,0.16)",
                      bgcolor: "rgba(255,255,255,0.03)",
                      "&:hover": {
                        color: "white",
                        bgcolor: alpha(theme.palette.common.white, 0.06),
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                      },
                    }}
                  >
                    Meus Ingressos
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<CreditCardRoundedIcon />}
                    onClick={() => navigate("/minhas-compras")}
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      color: "text.secondary",
                      borderColor: "rgba(255,255,255,0.16)",
                      bgcolor: "rgba(255,255,255,0.03)",
                      "&:hover": {
                        color: "white",
                        bgcolor: alpha(theme.palette.common.white, 0.06),
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                      },
                    }}
                  >
                    Minhas Compras
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Divider
            sx={{ borderColor: alpha(theme.palette.common.white, 0.1) }}
          />

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: { xs: 1, sm: 2 },
              minHeight: 56,
              "& .MuiTabs-indicator": {
                backgroundColor: theme.palette.primary.main,
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                color: "text.secondary",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 14,
                minHeight: 56,
                px: { xs: 1.5, sm: 2 },
                "&.Mui-selected": { color: "white" },
              },
            }}
          >
            <Tab
              label="Dados Pessoais"
              icon={<BadgeRoundedIcon />}
              iconPosition="start"
            />
            <Tab
              label="Endereços"
              icon={<LocationOnRoundedIcon />}
              iconPosition="start"
            />
            <Tab
              label="Segurança"
              icon={<SecurityRoundedIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* === CONTEÚDO DAS ABAS === */}
        <Box sx={{ minHeight: 400 }}>
          {/* ABA 0: DADOS PESSOAIS */}
          {tab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ ...glassCardSx, p: 4 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        Informações Básicas
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mt: 0.5 }}
                      >
                        Edite os campos e salve quando terminar.
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      startIcon={<SaveRoundedIcon />}
                      onClick={saveBasicProfile}
                      disabled={!dirtyBasic || savingBasic || loadingProfile}
                      sx={{
                        px: 3,
                        height: 40,
                        width: { md: 200, xs: 291 },
                        boxShadow: `0 0 22px ${alpha(theme.palette.primary.main, dirtyBasic ? 0.35 : 0.15)}`,
                      }}
                    >
                      {savingBasic ? "Salvando..." : "Salvar"}
                    </Button>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={12}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <TextField
                        fullWidth
                        label="Nome Completo"
                        value={form.name}
                        onChange={(e) =>
                          setField("name", formatFullName(e.target.value))
                        }
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <TextField
                        fullWidth
                        label="E-mail"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <TextField
                        fullWidth
                        label="Telefone / WhatsApp"
                        value={form.phone}
                        onChange={(e) =>
                          setField("phone", formatPhoneBR11(e.target.value))
                        }
                        inputProps={{ inputMode: "numeric" }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <TextField
                        fullWidth
                        label="CPF"
                        value={form.cpf}
                        onChange={(e) =>
                          setField("cpf", formatCPF(e.target.value))
                        }
                        inputProps={{ inputMode: "numeric" }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <TextField
                        fullWidth
                        label="Data de Nascimento"
                        value={form.birth}
                        onChange={(e) =>
                          setField("birth", formatBirthBR(e.target.value))
                        }
                        placeholder="DD/MM/AAAA"
                        inputProps={{ inputMode: "numeric" }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid container xs={12} md={4} sx={{ width: "100%" }}>
                <Paper
                  sx={{ ...glassCardSx, p: 3, height: "100%", width: "100%" }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Status da Conta
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: userData.isVerified
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.warning.main, 0.1),
                      border: `1px solid ${
                        userData.isVerified
                          ? alpha(theme.palette.primary.main, 0.3)
                          : alpha(theme.palette.warning.main, 0.3)
                      }`,
                      mb: 3,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {userData.isVerified ? (
                        <VerifiedUserRoundedIcon color="primary" />
                      ) : (
                        <SecurityRoundedIcon color="warning" />
                      )}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: "white" }}
                        >
                          {userData.isVerified
                            ? "Conta Verificada"
                            : "Verificação Pendente"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {userData.isVerified
                            ? "Você tem acesso total."
                            : "Confirme seu e-mail para segurança."}
                        </Typography>
                      </Box>
                    </Stack>

                    {!userData.isVerified && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        sx={{ mt: 2, width: "100%", borderRadius: 1, py: 1 }}
                        onClick={() =>
                          setToast({
                            open: true,
                            type: "info",
                            msg: "Plugar envio de verificação aqui.",
                          })
                        }
                      >
                        Enviar E-mail
                      </Button>
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Atalhos
                  </Typography>

                  <Stack spacing={1}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      fullWidth
                      startIcon={<LockRoundedIcon />}
                      onClick={() => setOpenPass(true)}
                      sx={{ borderRadius: 1, py: 1 }}
                    >
                      Trocar Senha
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      startIcon={<LogoutRoundedIcon />}
                      onClick={handleLogout}
                      sx={{ borderRadius: 1, py: 1 }}
                    >
                      Sair da Conta
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* ABA 1: ENDEREÇOS */}
          {tab === 1 && (
            <Paper sx={{ ...glassCardSx, p: 4, width: "100%", mx: "auto" }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
                sx={{ mb: 3 }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    Endereço Principal
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 0.5 }}
                  >
                    Edite e salve para atualizar seu cadastro.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  onClick={saveAddressToRTDB}
                  disabled={!dirtyAddress || savingAddress || loadingProfile}
                  sx={{
                    px: 3,
                    height: 40,
                    width: { md: 200, xs: 291 },
                    boxShadow: `0 0 22px ${alpha(theme.palette.primary.main, dirtyAddress ? 0.35 : 0.15)}`,
                  }}
                >
                  {savingAddress ? "Salvando..." : "Salvar"}
                </Button>
              </Stack>

              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={7}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <TextField
                    label="CEP"
                    fullWidth
                    value={address.cep}
                    onChange={(e) => setAddr("cep", formatCEP(e.target.value))}
                    inputProps={{ inputMode: "numeric" }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={7}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <TextField
                    label="Rua / Logradouro"
                    fullWidth
                    value={address.street}
                    onChange={(e) => setAddr("street", e.target.value)}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <TextField
                    label="Nº"
                    fullWidth
                    value={address.number}
                    onChange={(e) => setAddr("number", e.target.value)}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <TextField
                    label="Bairro"
                    fullWidth
                    value={address.district}
                    onChange={(e) => setAddr("district", e.target.value)}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <TextField
                    label="Complemento"
                    fullWidth
                    value={address.complement}
                    onChange={(e) => setAddr("complement", e.target.value)}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={8}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <TextField
                    label="Cidade"
                    fullWidth
                    value={address.city}
                    onChange={(e) => setAddr("city", e.target.value)}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <TextField
                    select
                    label="Estado (UF)"
                    sx={{ width: { md: 243, xs: 291 } }}
                    value={address.state}
                    onChange={(e) => setAddr("state", e.target.value)}
                  >
                    {BR_UFS.map((s) => (
                      <MenuItem key={s.uf} value={s.uf}>
                        {s.uf} — {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* ABA 2: SEGURANÇA */}
          {tab === 2 && (
            <Stack spacing={3} mx="auto">
              <Paper sx={{ ...glassCardSx, p: 3 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "start", md: "center" }}
                  spacing={2}
                >
                  <Box
                    sx={{
                      p: { md: 1.5, xs: 0 },
                      color: theme.palette.primary.main,
                    }}
                  >
                    <LockRoundedIcon />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">Senha e Acesso</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Recomendamos trocar sua senha a cada 3 meses.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => setOpenPass(true)}
                    sx={{ borderRadius: 1, width: { xs: "100%", md: "auto" } }}
                  >
                    Alterar Senha
                  </Button>
                </Stack>
              </Paper>

              <Paper
                sx={{
                  ...glassCardSx,
                  p: 3,
                  borderColor: alpha(theme.palette.error.main, 0.3),
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "start", md: "center" }}
                  spacing={2}
                >
                  <Box
                    sx={{
                      p: { md: 1.5, xs: 0 },
                      borderRadius: 2,
                    }}
                  >
                    <DeleteForeverRoundedIcon color="error" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" color="error">
                      Zona de Perigo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      A exclusão da conta é irreversível.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="error"
                    sx={{ borderRadius: 1, width: { xs: "100%", md: "auto" } }}
                    onClick={() =>
                      setToast({
                        open: true,
                        type: "info",
                        msg: "Fluxo de exclusão",
                      })
                    }
                  >
                    Excluir Conta
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          )}
        </Box>

        {/* === DIALOG TROCA DE SENHA === */}
        <Dialog
          open={openPass}
          onClose={() => setOpenPass(false)}
          PaperProps={{
            sx: {
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
              bgcolor: "#08162b",
              backgroundImage: "none",
            },
          }}
        >
          <DialogTitle>Alterar Senha</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1, minWidth: { sm: 400 } }}>
              <TextField
                label="Senha Atual"
                type="password"
                fullWidth
                value={pass.current}
                onChange={(e) =>
                  setPass((p) => ({ ...p, current: e.target.value }))
                }
              />
              <TextField
                label="Nova Senha"
                type="password"
                fullWidth
                value={pass.next}
                onChange={(e) =>
                  setPass((p) => ({ ...p, next: e.target.value }))
                }
              />
              <TextField
                label="Confirmar Senha"
                type="password"
                fullWidth
                value={pass.confirm}
                onChange={(e) =>
                  setPass((p) => ({ ...p, confirm: e.target.value }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenPass(false)} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleChangePassword}
              variant="contained"
              color="primary"
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {/* === TOAST === */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={toast.type}
            variant="filled"
            onClose={() => setToast((t) => ({ ...t, open: false }))}
          >
            {toast.msg}
          </Alert>
        </Snackbar>

        <Dialog
          open={openAvatarModal}
          onClose={() => setOpenAvatarModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Escolha seu ícone de perfil</DialogTitle>

          <DialogContent>
            <Grid container spacing={2}>
              {Object.entries(AVATAR_ICONS).map(([key, Icon]) => {
                const selected = avatarIcon === key;

                return (
                  <Grid item xs={3} sm={2} key={key}>
                    <Box
                      onClick={() => setAvatarIcon(key)}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        cursor: "pointer",
                        bgcolor: selected
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.common.white, 0.05),
                        border: selected
                          ? `2px solid ${theme.palette.primary.main}`
                          : "1px solid rgba(255,255,255,0.15)",
                        transition: "all .2s",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <Icon size={26} />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenAvatarModal(false)} color="inherit">
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                await saveAvatarIcon();
                setOpenAvatarModal(false);
              }}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
